import { Button, Chip, MenuItem, Select } from '@mui/material';
import { Box } from '@mui/system';
import LastSavedIndicator from 'components/LastSavedIndicator/LastSavedIndicator';
import StatusButton from './StatusButton';
import { useSaveFormTemplate } from 'hooks/useSaveFormTemplate';
import {
  fieldTemplatesState,
  formTemplateState,
  formTemplateVersionsState,
  isFormSavingAtom,
  layoutSelector,
} from 'state';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { FormTemplateStatus } from 'api/models/FormTemplate';
import FormEditorSaveButton from './FormEditorSaveButton';
import { useNavigate } from 'react-router-dom';

interface FormEditorControlsProps {
  disabled?: boolean;
}

export default function FormEditorControls({
  disabled,
}: FormEditorControlsProps) {
  const [formTemplate, setFormTemplate] = useRecoilState(formTemplateState);
  const setLayout = useSetRecoilState(layoutSelector);
  const setFieldTemplates = useSetRecoilState(fieldTemplatesState);
  const formTemplateVersions = useRecoilValue(formTemplateVersionsState);
  const { t } = useTranslation();
  const { save } = useSaveFormTemplate();
  const [loading, setLoading] = useRecoilState(isFormSavingAtom);
  const navigate = useNavigate();

  const isSelectDisplayed = formTemplate?.status !== undefined;
  const isFormTheLastInArray =
    formTemplateVersions[formTemplateVersions.length - 1]?.id ===
    formTemplate?.id;
  const isFinalizeDisabled =
    formTemplate?.status === FormTemplateStatus.Published ||
    !isFormTheLastInArray;

  return (
    <Box
      display="flex"
      flexDirection={'row'}
      width="100%"
      justifyContent={'end'}
      alignItems={'center'}
    >
      {!disabled && <LastSavedIndicator date={formTemplate?.updatedAt} />}
      {isSelectDisplayed && (
        <Select
          sx={{
            ml: 2,
            mr: 2,
            '&:before': {
              borderBottom: 'none',
            },
          }}
          size="small"
          value={
            formTemplate?.id ??
            formTemplateVersions[formTemplateVersions.length - 1].id
          }
          onChange={event => {
            const template = formTemplateVersions.find(
              formTemplate => formTemplate.id === event.target.value,
            );
            setFormTemplate(template);
            setLayout({
              children: template?.layout.items ?? [],
            });
            setFieldTemplates(template?.fieldTemplates ?? []);
          }}
        >
          {formTemplateVersions.map((formTemplate, index) => (
            <MenuItem
              key={formTemplate.id}
              value={formTemplate.id}
              sx={{ color: theme => theme.palette.text.primary }}
            >
              <Chip
                label={`${
                  formTemplate.status === FormTemplateStatus.Draft
                    ? ''
                    : `${formTemplate.version}.`
                } ${t(`forms.formTemplateStatus.${formTemplate.status}`)}`}
                color={
                  formTemplate.status === FormTemplateStatus.Draft
                    ? 'warning'
                    : 'success'
                }
              />
            </MenuItem>
          ))}
        </Select>
      )}

      {disabled && (
        <Button
          variant="contained"
          color="warning"
          onClick={() => navigate(-1)}
        >
          {t('general.cancel')}
        </Button>
      )}

      {!disabled && (
        <StatusButton
          disabled={isFinalizeDisabled || loading}
          onClick={async () => {
            setLoading(true);
            await save({
              finalize: true,
            });
            setLoading(false);
          }}
        />
      )}
      {!disabled && (
        <FormEditorSaveButton
          onClick={async () => {
            setLoading(true);
            await save();
            setLoading(false);
          }}
          disabled={!isFormTheLastInArray || loading}
          label={
            isFormTheLastInArray &&
            formTemplate?.status === FormTemplateStatus.Published
              ? t('general.createNewDraft')
              : t('general.saveAsDraft')
          }
        />
      )}
    </Box>
  );
}
